import z from 'zod';
import { getEnvironmentConfig, getLogger, httpClientInterface } from '../../utils/core';
import { TransactionSubType } from '../transactions/transactionsTypes';
const log = getLogger('app-components/flux/invoice-actions/index.js');

const GenerateInvoiceResponseSchema = z.object({ documentId: z.string() });
const DownloadInvoiceResponseSchema = z.object({ url: z.string() });

export type InvoiceData = {
  invoice: {
    invoiceNo: number;
    dueOn: string;
    dueAmount: number;
    originalAmount: number;
    cpAccountId?: string;
    paymentId?: string;
    paymentClientSecret?: string;
    sentOn?: string;
    pdf: string;
    subtype: TransactionSubType;
  };
  from: {
    businessName: string;
    businessEmail?: string;
    businessPhoneNo?: string;
  };
  to: {
    client: string;
    clientCompanyName?: string;
  };
};

export const getInvoicePdfBlob = async (invoiceId: string, showReceipts?: boolean): Promise<Blob> => {
  try {
    const requestUrl = `${getEnvironmentConfig().services_url}/reports/generate-and-get-invoice`;
    const requestBody = { invoiceId, showReceipts: showReceipts ?? false };

    const response = await httpClientInterface.send('POST', requestUrl, requestBody, undefined, undefined, {
      responseType: 'blob',
    });
    log.debug('Generating invoice, response', response, response.data);

    return response.data as Blob;
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const getInvoicePDF = async (invoiceId: string, showReceipts?: boolean): Promise<string> => {
  try {
    let requestUrl = `${getEnvironmentConfig().services_url}/reports/generate-invoice`;
    const requestBody1 = { invoiceId, showReceipts: showReceipts ?? false };

    let response = await httpClientInterface.send('POST', requestUrl, requestBody1);
    log.debug('Generating invoice, response', response, response);
    const documentId = GenerateInvoiceResponseSchema.parse(response.data).documentId;

    // Currently, generating an invoice involves async processing, so we need to wait for the documentId to be available.
    // THere is no way to tell when the documentId is available, so we just wait for 3 seconds. Need to come up with
    // a better way to handle this.
    //
    await new Promise((resolve) => setTimeout(resolve, 3000));

    requestUrl = `${getEnvironmentConfig().services_url}/reports/download-invoice`;
    const requestBody2 = { documentId };

    response = await httpClientInterface.send('POST', requestUrl, requestBody2);
    log.debug('Retrieving invoice URL, response', response);

    return DownloadInvoiceResponseSchema.parse(response.data).url;
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const sendInvoiceEmail = async (
  invoiceId: string,
  subtype: TransactionSubType = TransactionSubType.Invoice,
  showReceipts?: boolean,
): Promise<void> => {
  try {
    let requestUrl: string;
    let requestBody: object;
    switch (subtype) {
      case TransactionSubType.InvoiceCreditMemo:
        requestUrl = `${getEnvironmentConfig().services_url}/reports/send-credit-memo`;
        requestBody = { creditMemoId: invoiceId, showReceipts: showReceipts ?? false };
        break;
      default:
        requestUrl = `${getEnvironmentConfig().services_url}/reports/send-invoice-email`;
        requestBody = { invoiceId, showReceipts: showReceipts ?? false };
        break;
    }

    const response = await httpClientInterface.send('POST', requestUrl, requestBody);
    log.debug('Sending email, response', response, response.data);
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const viewInvoiceData = async (JWToken: string) => {
  try {
    const requestUrl = `${getEnvironmentConfig().services_url}/reports/retrieve-invoice-info`;
    const response = (await httpClientInterface.send(
      'POST',
      requestUrl,
      { token: JWToken },
      { Authorization: null, 'qcs-dataset-id': null },
    )) as { data: InvoiceData };
    log.debug('viewInvoiceData response', response, response.data);

    return response.data;
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};
